import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { TourState } from './tour-state';
import { MapProvider } from '../map/map-provider';
import { ViewModeProvider } from '../map/view-mode-provider';
import { ViewMode } from '../../../../shared/enums/view-mode.enum';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import PointSymbol3D from '@arcgis/core/symbols/PointSymbol3D';
import IconSymbol3DLayer from '@arcgis/core/symbols/IconSymbol3DLayer';

@Injectable()
export class TourNotifier extends BaseNotifier<TourState> {
  private static readonly INITIAL: TourState = {
    active: false,
    orbiting: false,
    point: null,
    orbitSpeed: 'normal',
  };

  private clickHandle: { remove(): void } | null = null;
  private orbitTimer: ReturnType<typeof setTimeout> | null = null;
  private currentHeading = 0;

  private tourLayer: GraphicsLayer | null = null;
  private pulseGraphic: Graphic | null = null;
  private pulseInterval: ReturnType<typeof setInterval> | null = null;
  private viewWatcherHandle: { destroy(): void } | null = null;
  private lastViewForClick: MapView | SceneView | null = null;

  constructor(ref: Ref) {
    super(ref, TourNotifier.INITIAL);
  }

  private get mapProvider(): MapProvider {
    return this.ref.read(MapProvider);
  }

  private get viewModeProvider(): ViewModeProvider {
    return this.ref.read(ViewModeProvider);
  }

  private get view(): MapView | SceneView | null {
    return this.mapProvider.notifier.getActiveView();
  }

  private get is3d(): boolean {
    return this.viewModeProvider.state().mode === ViewMode.Scene;
  }

  // ── Public API ──────────────────────────────────────────────

  async toggle(): Promise<void> {
    if (this.snapshot().active) {
      this.deactivate();
    } else {
      await this.activate();
    }
  }

  async activate(): Promise<void> {
    if (this.snapshot().active) return;
    this.setState({ ...TourNotifier.INITIAL, active: true });
    this.ensureLayer();

    if (!this.is3d) {
      this.mapProvider.notifier.switchTo(ViewMode.Scene);
      this.viewModeProvider.notifier.setMode(ViewMode.Scene);
    }

    await this.waitForView();
    this.attachClickHandler();
    this.startViewWatcher();
  }

  deactivate(): void {
    this.stopOrbit();
    this.stopPulse();
    this.detachClickHandler();
    this.stopViewWatcher();
    this.clearGraphics();
    this.lastViewForClick = null;
    this.currentHeading = 0;
    this.setState({ ...TourNotifier.INITIAL });
  }

  setOrbitSpeed(speed: 'slow' | 'normal' | 'fast'): void {
    this.updateState(s => ({ ...s, orbitSpeed: speed }));
  }

  stopOrbit(): void {
    if (this.orbitTimer) {
      clearTimeout(this.orbitTimer);
      this.orbitTimer = null;
    }
    this.updateState(s => ({ ...s, orbiting: false }));
    const v = this.view;
    if (v && v.type === '3d') {
      (v as any).goTo((v as any).camera, { animate: false }).catch(() => {});
    }
  }

  // ── Wait for view ───────────────────────────────────────────

  private waitForView(): Promise<void> {
    return new Promise(resolve => {
      const check = () => {
        const v = this.view;
        if (v && v.type === '3d') { resolve(); return; }
        setTimeout(check, 100);
      };
      check();
    });
  }

  // ── View watcher ────────────────────────────────────────────

  private startViewWatcher(): void {
    this.stopViewWatcher();
    this.viewWatcherHandle = this.listen(() => {
      const v = this.view;
      if (v && v !== this.lastViewForClick && this.snapshot().active) {
        this.detachClickHandler();
        this.lastViewForClick = v;
        this.attachClickHandlerToView(v);
      }
    });
  }

  private stopViewWatcher(): void {
    if (this.viewWatcherHandle) {
      this.viewWatcherHandle.destroy();
      this.viewWatcherHandle = null;
    }
  }

  // ── Graphics layer ──────────────────────────────────────────

  private ensureLayer(): void {
    if (this.tourLayer) return;
    const map = this.mapProvider.state().map;
    if (!map) return;

    this.tourLayer = new GraphicsLayer({
      id: 'tour-flight-layer',
      title: 'Órbita 3D',
      listMode: 'hide',
      elevationInfo: { mode: 'on-the-ground' as const },
    });
    map.add(this.tourLayer);
  }

  private drawMarker(lon: number, lat: number, alt: number): void {
    if (!this.tourLayer) return;
    this.tourLayer.removeAll();
    this.stopPulse();
    this.pulseGraphic = null;

    const SR = { spatialReference: { wkid: 4326 } };

    const marker = new Graphic({
      geometry: new Point({ longitude: lon, latitude: lat, z: alt + 80, ...SR }),
      symbol: new PointSymbol3D({
        symbolLayers: [
          new IconSymbol3DLayer({
            resource: { primitive: 'circle' },
            material: { color: [0, 200, 255, 0.9] },
            outline: { color: [255, 255, 255, 0.9], size: 2 },
            size: 18,
          }),
        ],
      } as any),
    });

    this.tourLayer.add(marker);

    const pulse = new Graphic({
      geometry: new Point({ longitude: lon, latitude: lat, z: alt + 10, ...SR }),
      symbol: new PointSymbol3D({
        symbolLayers: [
          new IconSymbol3DLayer({
            resource: { primitive: 'circle' },
            material: { color: [0, 200, 255, 0] },
            outline: { color: [0, 200, 255, 0.5], size: 2 },
            size: 30,
          }),
        ],
      } as any),
    });

    this.tourLayer.add(pulse);
    this.pulseGraphic = pulse;
    this.startPulse();
  }

  private startPulse(): void {
    this.stopPulse();
    let visible = true;
    this.pulseInterval = setInterval(() => {
      visible = !visible;
      if (this.pulseGraphic) this.pulseGraphic.visible = visible;
    }, 550);
  }

  private stopPulse(): void {
    if (this.pulseInterval) {
      clearInterval(this.pulseInterval);
      this.pulseInterval = null;
    }
  }

  private clearGraphics(): void {
    this.stopPulse();
    if (this.tourLayer) this.tourLayer.removeAll();
    this.pulseGraphic = null;
  }

  // ── Click handling ──────────────────────────────────────────

  private attachClickHandler(): void {
    this.detachClickHandler();
    const v = this.view;
    if (!v) return;
    this.lastViewForClick = v;
    this.attachClickHandlerToView(v);
  }

  private attachClickHandlerToView(v: MapView | SceneView): void {
    const handle = v.on('click', (event: any) => {
      if (!this.snapshot().active) return;
      const mp = event.mapPoint;
      if (!mp) return;
      const lon = mp.longitude;
      const lat = mp.latitude;
      if (lon == null || lat == null) return;
      this.flyAndOrbit(lon, lat);
    });
    this.clickHandle = handle;
  }

  private detachClickHandler(): void {
    if (this.clickHandle) {
      this.clickHandle.remove();
      this.clickHandle = null;
    }
  }

  // ── Query elevation (replicating XXXXX pattern) ─────────────

  private async queryElevation(lon: number, lat: number): Promise<number> {
    const v = this.view;
    if (!v || v.type !== '3d') return 0;

    const SR = { spatialReference: { wkid: 4326 } };
    const point = new Point({ x: lon, y: lat, ...SR });

    try {
      const sv = v as any;
      const result = await sv.map.ground.queryElevation(point);
      return Math.max(result?.geometry?.z || 0, 0);
    } catch {
      return 0;
    }
  }

  // ── Fly + Orbit (replicating XXXXX tour-renderer-service) ───

  private async flyAndOrbit(lon: number, lat: number): Promise<void> {
    const v = this.view;
    if (!v || v.type !== '3d') return;

    this.stopOrbit();
    this.updateState(s => ({ ...s, point: { lon, lat } }));

    const sv = v as any;
    const SR = { spatialReference: { wkid: 4326 } };

    const baseAlt = await this.queryElevation(lon, lat);

    this.drawMarker(lon, lat, baseAlt);

    try {
      await sv.goTo(
        {
          target: new Point({ x: lon, y: lat, z: baseAlt + 450, ...SR }),
          heading: 0,
          tilt: 60,
          zoom: 16,
        },
        { duration: 3500, easing: 'in-out-expo' },
      );
    } catch { /* ignore */ }

    this.updateState(s => ({ ...s, orbiting: true }));
    this.currentHeading = (sv.camera?.heading ?? 0) + 90;
    const orbitPoint = new Point({ x: lon, y: lat, z: baseAlt + 80, ...SR });
    this.runOrbitStep(orbitPoint);
  }

  private runOrbitStep(target: Point): void {
    if (!this.snapshot().orbiting) return;

    const v = this.view;
    if (!v || v.type !== '3d') return;

    const orbitDurations = { slow: 13000, normal: 9000, fast: 6000 };
    const sv = v as any;

    sv.goTo(
      {
        target,
        heading: this.currentHeading,
        tilt: 60,
        zoom: 16,
      },
      {
        duration: orbitDurations[this.snapshot().orbitSpeed],
        easing: 'linear',
      },
    )
      .then(() => {
        this.currentHeading = (this.currentHeading + 90) % 360;
        if (this.snapshot().orbiting) {
          this.orbitTimer = setTimeout(() => this.runOrbitStep(target), 50);
        }
      })
      .catch(() => {
        this.updateState(s => ({ ...s, orbiting: false }));
      });
  }

  // ── Lifecycle ───────────────────────────────────────────────

  protected override onDispose(): void {
    this.stopOrbit();
    this.stopPulse();
    this.detachClickHandler();
    this.stopViewWatcher();
    this.clearGraphics();
    this.lastViewForClick = null;
    this.currentHeading = 0;
    if (this.tourLayer) {
      try { this.tourLayer.destroy(); } catch {}
      this.tourLayer = null;
    }
  }
}
